import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as blazeface from '@tensorflow-models/blazeface';
import '@tensorflow/tfjs';
import { logViolation } from '../api';

export default function FaceProctoring({
  token,
  onViolation,
  precheck = false,
  onReady = () => {},
  hasPermissions = false,
  onSnapshot,
  maxViolations = 0,
  violationCount = 0,
  isCriticalViolations = false,
  badgeState,
  badgeLabel,
  onFaceDetectionStatus,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [model, setModel] = useState(null);
  const [violations, setViolations] = useState(0);
  const stableFramesRef = useRef(0);
  const readyCalledRef = useRef(false);
  const [precheckProgress, setPrecheckProgress] = useState(0);
  const sideFaceCountRef = useRef(0);
  const forwardFaceCountRef = useRef(0);
  const monitoringStartTimeRef = useRef(null);
  const isInitializingRef = useRef(true);
  const noFaceCountRef = useRef(0);
  const lastViolationTimeRef = useRef(0);
  const multipleFacesCountRef = useRef(0);
  const lastFaceStatusRef = useRef(null);
  const detectionFailureCountRef = useRef(0);
  const lastSuccessfulDetectionRef = useRef(null);
  const [isLookingAtCamera, setIsLookingAtCamera] = useState(false);
  const [hasFaceDetected, setHasFaceDetected] = useState(false);
  const [detectionStatus, setDetectionStatus] = useState('initializing');
  const hasFaceDetectedRef = useRef(false);
  const detectionStatusRef = useRef('initializing');

  // Debug: Log props to verify they're being passed (only in development, first render only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const propsInfo = {
        token: token ? 'provided' : 'undefined (optional)',
        onViolation: typeof onViolation === 'function' ? 'provided' : 'undefined (optional)',
        precheck,
        hasPermissions,
        onSnapshot: typeof onSnapshot === 'function' ? 'provided' : 'undefined (optional)',
        maxViolations: maxViolations || '0 (precheck mode)',
        violationCount,
        isCriticalViolations,
        badgeState: badgeState || 'undefined (optional)',
        badgeLabel: badgeLabel || 'undefined (optional)',
        onFaceDetectionStatus: typeof onFaceDetectionStatus === 'function' ? 'provided' : 'undefined (optional)',
      };
      console.log('FaceProctoring initialized:', propsInfo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debug: Track hasFaceDetected changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('hasFaceDetected changed:', hasFaceDetected);
    }
  }, [hasFaceDetected]);

  const captureSnapshot = useCallback(() => {
    if (!videoRef.current) return null;
    const video = videoRef.current;
    const width = video.videoWidth || video.width || 0;
    const height = video.videoHeight || video.height || 0;
    if (!width || !height) return null;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, width, height);
    try {
      return canvas.toDataURL('image/jpeg', 0.85);
    } catch (err) {
      console.warn('Snapshot capture failed', err);
      return null;
    }
  }, []);

  // Setup BlazeFace model and camera
  useEffect(() => {
    if (!hasPermissions) {
      // Reset state when permissions are not granted
      setDetectionStatus('initializing');
      detectionStatusRef.current = 'initializing';
      setHasFaceDetected(false);
      hasFaceDetectedRef.current = false;
      return;
    }

    let mounted = true;

    const setup = async () => {
      try {
        // Reset detection status when starting setup
        setDetectionStatus('initializing');
        detectionStatusRef.current = 'initializing';
        
        const loadedModel = await blazeface.load();
        if (!mounted) return;

        setModel(loadedModel);
        // Status will be set to 'ready' when video is ready

        // Setup camera
        try {
          // Stop any existing stream before requesting new one
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          if (videoRef.current && videoRef.current.srcObject) {
            const oldStream = videoRef.current.srcObject;
            oldStream.getTracks().forEach(track => track.stop());
            videoRef.current.srcObject = null;
          }

          const stream = await navigator.mediaDevices.getUserMedia({
            video: { width: 640, height: 480 },
            audio: true
          });

          if (!mounted) {
            stream.getTracks().forEach(track => track.stop());
            return;
          }

          streamRef.current = stream;

          if (videoRef.current) {
            const video = videoRef.current;
            video.setAttribute('playsinline', 'true');
            video.setAttribute('webkit-playsinline', 'true');
            video.setAttribute('autoplay', 'true');
            video.setAttribute('muted', 'true');
            video.srcObject = stream;

            const handleLoadedMetadata = () => {
              if (mounted && video.readyState >= 2) {
                setDetectionStatus('ready');
                detectionStatusRef.current = 'ready';
              }
              if (video.videoWidth > 0 && video.videoHeight > 0) {
                video.play().catch(err => {
                  console.error('Video play error:', err);
                });
              } else {
                setTimeout(() => {
                  if (video.videoWidth > 0 && video.videoHeight > 0) {
                    video.play().catch(err => {
                      console.error('Video play error:', err);
                    });
                  }
                }, 100);
              }
            };

            video.onloadedmetadata = () => {
              handleLoadedMetadata();
              // Update status when metadata is loaded
              if (mounted && video.readyState >= 2) {
                setDetectionStatus('ready');
                detectionStatusRef.current = 'ready';
              }
            };
            
            video.onloadeddata = () => {
              if (video.videoWidth > 0 && video.videoHeight > 0 && video.paused) {
                video.play().catch(err => {
                  console.error('Video play error:', err);
                });
              }
              // Update status when video data is loaded
              if (mounted && video.readyState >= 2) {
                setDetectionStatus('ready');
                detectionStatusRef.current = 'ready';
              }
            };

            // Update status when video starts playing
            video.onplaying = () => {
              if (mounted) {
                setDetectionStatus('ready');
                detectionStatusRef.current = 'ready';
              }
            };
            
            // Also update status on canplay event
            video.oncanplay = () => {
              if (mounted && video.readyState >= 2) {
                setDetectionStatus('ready');
                detectionStatusRef.current = 'ready';
              }
            };

            isInitializingRef.current = true;
            monitoringStartTimeRef.current = null;
            sideFaceCountRef.current = 0;
            forwardFaceCountRef.current = 0;
            
            // Force play and check status after a short delay to ensure video is ready
            let retryCount = 0;
            const maxRetries = 15; // Check up to 15 times (3 seconds total)
            
            const checkVideoReady = () => {
              if (!mounted) return;
              
              const currentVideo = videoRef.current;
              if (currentVideo && currentVideo.srcObject) {
                // Try to play if paused
                if (currentVideo.paused) {
                  currentVideo.play().catch(err => {
                    console.error('Video play error after delay:', err);
                  });
                }
                
                // Check if video is ready and update status
                const isReady = currentVideo.readyState >= 2 && 
                               currentVideo.videoWidth > 0 && 
                               currentVideo.videoHeight > 0 &&
                               currentVideo.srcObject.active;
                
                if (isReady) {
                  setDetectionStatus('ready');
                  detectionStatusRef.current = 'ready';
                  console.log('✅ Video is ready - status set to ready');
                  return; // Video is ready, stop checking
                }
              }
              
              // If not ready yet and haven't exceeded max retries, check again
              if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(checkVideoReady, 200);
              } else {
                // After max retries, set to ready anyway if stream exists and is active
                const currentVideo = videoRef.current;
                if (currentVideo && currentVideo.srcObject && currentVideo.srcObject.active) {
                  setDetectionStatus('ready');
                  detectionStatusRef.current = 'ready';
                  console.log('✅ Video stream is active - forcing status to ready after max retries');
                } else {
                  console.warn('⚠️ Video not ready after max retries:', {
                    hasVideo: !!currentVideo,
                    hasSrcObject: !!currentVideo?.srcObject,
                    streamActive: currentVideo?.srcObject?.active,
                    readyState: currentVideo?.readyState,
                    videoWidth: currentVideo?.videoWidth,
                    videoHeight: currentVideo?.videoHeight
                  });
                }
              }
            };
            
            // Start checking immediately and also after delays
            checkVideoReady();
            setTimeout(checkVideoReady, 500);
            setTimeout(checkVideoReady, 1000);
          }
        } catch (cameraError) {
          console.error('Camera access error', cameraError);
          setDetectionStatus('error');
          detectionStatusRef.current = 'error';
          if (onViolation) onViolation('camera-access-denied', { error: cameraError });
        }
      } catch (error) {
        console.error('Failed to load BlazeFace model:', error);
        setDetectionStatus('error');
        detectionStatusRef.current = 'error';
      }
    };

    setup();

    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }
    };
  }, [hasPermissions, onViolation]);

  // FIXED: Unified face detection state management
  const updateFaceDetectionState = useCallback((hasFace) => {
    console.log('updateFaceDetectionState called with:', hasFace);
    
    // Update ref immediately
    hasFaceDetectedRef.current = hasFace;
    
    // Update state with functional update to avoid race conditions
    setHasFaceDetected(prev => {
      if (prev !== hasFace) {
        console.log('🔄 Updating hasFaceDetected from', prev, 'to', hasFace);
        return hasFace;
      }
      return prev;
    });
    
    // Update detection status based on face presence
    setDetectionStatus(prev => {
      let newStatus = prev;
      if (hasFace && prev !== 'running') {
        console.log('🔄 Setting detectionStatus to running');
        newStatus = 'running';
      } else if (!hasFace && prev === 'running') {
        console.log('🔄 Setting detectionStatus to ready (no face)');
        newStatus = 'ready';
      } else if (prev === 'initializing') {
        // If still initializing and we have video, set to ready
        newStatus = 'ready';
      }
      detectionStatusRef.current = newStatus;
      return newStatus;
    });
  }, []);

  // FIXED: Precheck mode handler
  const handlePrecheck = useCallback((predictions) => {
    console.log('handlePrecheck called with:', {
      predictions: predictions ? `Array(${predictions.length})` : 'null/undefined',
      isArray: Array.isArray(predictions),
      length: predictions?.length
    });

    // Validate predictions: must have exactly one face with valid data
    const hasValidFace = Array.isArray(predictions) && 
                        predictions.length === 1 && 
                        predictions[0] && 
                        predictions[0].topLeft && 
                        predictions[0].bottomRight &&
                        Array.isArray(predictions[0].topLeft) &&
                        Array.isArray(predictions[0].bottomRight) &&
                        predictions[0].topLeft.length >= 2 &&
                        predictions[0].bottomRight.length >= 2;

    if (!hasValidFace) {
      console.log('handlePrecheck: Invalid predictions - resetting');
      stableFramesRef.current = 0;
      setPrecheckProgress(0);
      // Only set to false if we got an empty array (explicitly no faces)
      if (Array.isArray(predictions) && predictions.length === 0) {
        updateFaceDetectionState(false);
      }
      return;
    }

    const face = predictions[0];
    console.log('handlePrecheck: Face is VALID - updating state');

    // Face is valid - increment stable frames
    stableFramesRef.current++;
    const progress = Math.min(stableFramesRef.current, 30);
    setPrecheckProgress(progress);
    
    // Update face detection state to true
    updateFaceDetectionState(true);
    
    console.log('handlePrecheck: State update called', {
      stableFrames: stableFramesRef.current,
      progress,
      hasFace: true
    });

    // Face must be stable for 30 frames (~1 second at 30fps) before completing
    if (stableFramesRef.current >= 30 && !readyCalledRef.current) {
      readyCalledRef.current = true;
      setPrecheckProgress(30);
      const snapshot = captureSnapshot();
      if (onSnapshot) onSnapshot(snapshot);
      onReady(snapshot);
    }
  }, [onReady, captureSnapshot, onSnapshot, updateFaceDetectionState]);

  // Record violation function
  const record = useCallback((type, meta = {}) => {
    setViolations((v) => v + 1);
    if (onViolation) {
      onViolation(type, meta);
    }
    if (token) {
      logViolation(token, type, meta).catch(() => {});
    }
  }, [onViolation, token]);

  // Detect face orientation
  const detectFaceOrientation = useCallback((face, video) => {
    if (!face || !video) return 'unknown';

    const landmarks = face.landmarks;

    if (!landmarks || !Array.isArray(landmarks) || landmarks.length < 6) {
      return 'no-landmarks';
    }

    const rightEye = landmarks[0];
    const leftEye = landmarks[1];

    if (!rightEye || !leftEye ||
        !Array.isArray(rightEye) || !Array.isArray(leftEye) ||
        rightEye.length < 2 || leftEye.length < 2 ||
        typeof rightEye[0] !== 'number' || typeof rightEye[1] !== 'number' ||
        typeof leftEye[0] !== 'number' || typeof leftEye[1] !== 'number') {
      return 'eyes-not-detected';
    }

    const videoWidth = video.videoWidth || video.width || 0;
    const videoHeight = video.videoHeight || video.height || 0;

    if (!videoWidth || !videoHeight) return 'unknown';

    const rightEyeX = rightEye[0] * videoWidth;
    const rightEyeY = rightEye[1] * videoHeight;
    const leftEyeX = leftEye[0] * videoWidth;
    const leftEyeY = leftEye[1] * videoHeight;

    const eyeDistance = Math.sqrt(
      Math.pow(leftEyeX - rightEyeX, 2) + Math.pow(leftEyeY - rightEyeY, 2)
    );

    if (!face.topLeft || !face.bottomRight ||
        !Array.isArray(face.topLeft) || !Array.isArray(face.bottomRight) ||
        face.topLeft.length < 2 || face.bottomRight.length < 2) {
      return 'invalid-face-box';
    }

    const [x1, y1] = face.topLeft;
    const [x2, y2] = face.bottomRight;

    if (typeof x1 !== 'number' || typeof y1 !== 'number' ||
        typeof x2 !== 'number' || typeof y2 !== 'number') {
      return 'invalid-face-box';
    }

    const faceWidth = Math.abs(x2 - x1);
    const faceHeight = Math.abs(y2 - y1);

    if (faceWidth === 0 || faceHeight === 0) {
      return 'invalid-face-box';
    }

    const eyeDistanceRatio = eyeDistance / faceWidth;

    // More lenient: only flag if eyes are very close together (extreme side view)
    if (eyeDistanceRatio < 0.15) {
      return 'side-face-too-close';
    }

    const eyeVerticalDiff = Math.abs(leftEyeY - rightEyeY);
    // More lenient: allow more vertical difference (tilted head)
    const maxVerticalDiff = faceHeight * 0.45;

    if (eyeVerticalDiff > maxVerticalDiff) {
      return 'face-tilted';
    }

    const faceCenterX = (x1 + x2) / 2;
    const rightEyeOffset = Math.abs(rightEyeX - faceCenterX);
    const leftEyeOffset = Math.abs(leftEyeX - faceCenterX);
    const eyeOffsetDiff = Math.abs(rightEyeOffset - leftEyeOffset);

    // More lenient: only flag if there's a significant difference (more than 50% of face width)
    // This allows natural head movements without triggering violations
    if (eyeOffsetDiff > faceWidth * 0.50) {
      return 'side-profile';
    }

    return 'forward';
  }, []);

  // Detect looking away
  const detectLookingAway = useCallback((face, video) => {
    if (!face || !face.topLeft || !face.bottomRight || !video) return;

    const [x1, y1] = face.topLeft;
    const [x2, y2] = face.bottomRight;

    if (!x1 || !x2 || !y1 || !y2) return;

    const videoWidth = video.videoWidth || video.width || 0;
    const videoHeight = video.videoHeight || video.height || 0;

    if (!videoWidth || !videoHeight) return;

    const faceCenterX = (x1 + x2) / 2;
    const faceCenterY = (y1 + y2) / 2;

    const frameCenterX = videoWidth / 2;
    const frameCenterY = videoHeight / 2;

    const offsetX = Math.abs(faceCenterX - frameCenterX);
    const offsetY = Math.abs(faceCenterY - frameCenterY);

    const thresholdX = videoWidth * 0.3;
    const thresholdY = videoHeight * 0.3;

    if (offsetX > thresholdX || offsetY > thresholdY) {
      const now = Date.now();
      if (now - lastViolationTimeRef.current > 3000) {
        record('looking-away');
        lastViolationTimeRef.current = now;
      }
    }
  }, [record]);

  // FIXED: Monitoring mode handler
  const handleMonitoring = useCallback((predictions, video) => {
    const now = Date.now();
    const MIN_VIOLATION_INTERVAL = 2000;

    // Debug: Log predictions received
    if (process.env.NODE_ENV === 'development') {
      console.log('📹 handleMonitoring called:', {
        predictions: predictions ? `Array(${predictions.length})` : 'null/undefined',
        isArray: Array.isArray(predictions),
        length: predictions?.length,
        firstFace: predictions?.[0] ? {
          hasTopLeft: !!predictions[0].topLeft,
          hasBottomRight: !!predictions[0].bottomRight,
          hasLandmarks: !!predictions[0].landmarks
        } : 'no face'
      });
    }

    // Validate predictions first
    const hasValidFace = Array.isArray(predictions) && 
                        predictions.length === 1 && 
                        predictions[0] && 
                        predictions[0].topLeft && 
                        predictions[0].bottomRight;

    // Debug: Log validation result
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Face validation result:', hasValidFace);
    }

    // Update face detection state based on predictions
    updateFaceDetectionState(hasValidFace);

    if (!predictions || predictions.length === 0) {
      noFaceCountRef.current++;
      // Only record violation after multiple consecutive failures to prevent false positives
      if (noFaceCountRef.current >= 3 && (now - lastViolationTimeRef.current > 1000)) {
        record('no-face');
        lastViolationTimeRef.current = now;
        noFaceCountRef.current = 0; // Reset after recording
      }
      setIsLookingAtCamera(false);
      return;
    }

    noFaceCountRef.current = 0;

    if (predictions.length > 1) {
      multipleFacesCountRef.current++;
      // Multiple faces - still a face detected, but it's a violation
      updateFaceDetectionState(true); // Still has face, just multiple
      if (multipleFacesCountRef.current >= 2 && (now - lastViolationTimeRef.current > MIN_VIOLATION_INTERVAL)) {
        record('multiple-faces', { faceCount: predictions.length });
        lastViolationTimeRef.current = now;
        multipleFacesCountRef.current = 0;
      }
      setIsLookingAtCamera(false);
      return;
    }

    multipleFacesCountRef.current = 0;

    const face = predictions[0];
    if (!face || !face.topLeft || !face.bottomRight) {
      // Invalid face data - set to false
      updateFaceDetectionState(false);
      record('no-face');
      setIsLookingAtCamera(false);
      return;
    }

    // Valid face detected - ensure state is true
    updateFaceDetectionState(true);

    // Side-face detection removed - always treat face as forward
    // Reset side face counter
    sideFaceCountRef.current = 0;
    forwardFaceCountRef.current++;
    
    // Check for looking away (face position in frame) - this checks if face is centered
    if (forwardFaceCountRef.current >= 3) {
      detectLookingAway(face, video);
      setIsLookingAtCamera(true);
    }
  }, [record, detectLookingAway, updateFaceDetectionState]);

  // FIXED: Main detection loop with unified state management
  useEffect(() => {
    if (!model) return;
    
    // If video is not ready yet, wait for it and update status
    if (!videoRef.current) {
      // Check periodically for video element
      const checkVideo = setInterval(() => {
        const video = videoRef.current;
        if (video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
          setDetectionStatus('ready');
          clearInterval(checkVideo);
        }
      }, 300);
      return () => clearInterval(checkVideo);
    }

    // Debug: Log when effect runs
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Detection loop effect running with precheck:', precheck);
    }

    let rafId = null;
    let mounted = true;

    const detect = async () => {
      if (!mounted) return;

      try {
        const video = videoRef.current;
        if (!video) {
          rafId = requestAnimationFrame(detect);
          return;
        }

        const videoWidth = video.videoWidth || video.width || 0;
        const videoHeight = video.videoHeight || video.height || 0;

        // Update status to ready as soon as video has dimensions and is ready
        if (video.readyState >= 2 && videoWidth > 0 && videoHeight > 0) {
          if (detectionStatusRef.current === 'initializing' || detectionStatusRef.current !== 'ready') {
            setDetectionStatus('ready');
            detectionStatusRef.current = 'ready';
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Detection loop: Video is ready, status updated to ready');
            }
          }
        }

        if (!videoWidth || !videoHeight || videoWidth === 0 || videoHeight === 0) {
          rafId = requestAnimationFrame(detect);
          return;
        }

        if (video.readyState < 2) {
          rafId = requestAnimationFrame(detect);
          return;
        }

        if (!video.srcObject || !video.srcObject.active) {
          rafId = requestAnimationFrame(detect);
          return;
        }

        // Ensure video is playing
        if (video.paused) {
          try {
            await video.play();
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            console.error('Failed to play video:', err);
            setTimeout(() => {
              if (video && video.paused) {
                video.play().catch(() => {});
              }
            }, 500);
          }
        }
        
        // Update status to ready once video is ready (don't wait for face detection)
        if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
          if (detectionStatusRef.current === 'initializing' || detectionStatusRef.current !== 'ready') {
            setDetectionStatus('ready');
            detectionStatusRef.current = 'ready';
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Detection loop: Video ready, status updated to ready');
            }
          }
        }
        
        if (!video.videoWidth || !video.videoHeight || video.readyState < 2) {
          // If video is not ready yet, keep status as initializing
          if (detectionStatusRef.current !== 'initializing' && detectionStatus !== 'initializing') {
            setDetectionStatus('initializing');
            detectionStatusRef.current = 'initializing';
          }
          rafId = requestAnimationFrame(detect);
          return;
        }

        // Grace period
        if (isInitializingRef.current) {
          if (!monitoringStartTimeRef.current) {
            monitoringStartTimeRef.current = Date.now();
          }

          const timeSinceStart = Date.now() - monitoringStartTimeRef.current;
          const gracePeriod = 2000;

          if (timeSinceStart < gracePeriod) {
            if (precheck) {
              try {
                const preds = await model.estimateFaces(video, false);
                if (preds !== null && preds !== undefined && Array.isArray(preds) && preds.length > 0) {
                  handlePrecheck(preds);
                }
              } catch (err) {
                // Ignore errors during initialization
              }
            }
            rafId = requestAnimationFrame(detect);
            return;
          } else {
            isInitializingRef.current = false;
            sideFaceCountRef.current = 0;
            forwardFaceCountRef.current = 0;
            noFaceCountRef.current = 0;
            multipleFacesCountRef.current = 0;
          }
        }

        // Face detection with retry logic
        let preds = null;
        let retryCount = 0;
        const MAX_RETRIES = 3;
        
        while (retryCount < MAX_RETRIES && (preds === null || preds === undefined)) {
          try {
            if (!video || !video.videoWidth || !video.videoHeight || video.readyState < 2) {
              if (process.env.NODE_ENV === 'development' && retryCount === 0) {
                console.log('⚠️ Video not ready for detection:', {
                  hasVideo: !!video,
                  videoWidth: video?.videoWidth,
                  videoHeight: video?.videoHeight,
                  readyState: video?.readyState
                });
              }
              break;
            }
            
            preds = await model.estimateFaces(video, false);
            
            // Debug: Log detection results
            if (process.env.NODE_ENV === 'development' && retryCount === 0) {
              console.log('🎯 Face detection result:', {
                predictions: preds ? `Array(${preds.length})` : 'null/undefined',
                isArray: Array.isArray(preds),
                length: preds?.length,
                precheck,
                actualPrecheck: precheck, // Ensure we're using the current value
                isInitializing: isInitializingRef.current
              });
            }
            
            if (preds !== null && preds !== undefined) {
              break;
            }
          } catch (modelError) {
            if (modelError.message && !modelError.message.includes('height') && retryCount === MAX_RETRIES - 1) {
              console.error('Model estimation error after retries:', modelError);
            }
            retryCount++;
            
            if (retryCount < MAX_RETRIES) {
              await new Promise(resolve => setTimeout(resolve, 50));
            }
          }
        }

        // If still no predictions after retries, track failure
        if (preds === null || preds === undefined) {
          detectionFailureCountRef.current++;
          
          // Only set hasFaceDetected to false if we've had many consecutive failures
          if (detectionFailureCountRef.current > 15) { // Increased threshold
            updateFaceDetectionState(false);
          }
          
          if (detectionFailureCountRef.current > 30 && detectionFailureCountRef.current % 30 === 0) {
            console.warn('Face detection failing repeatedly.');
          }
          
          rafId = requestAnimationFrame(detect);
          return;
        }

        // Reset failure counter on successful detection
        detectionFailureCountRef.current = 0;
        lastSuccessfulDetectionRef.current = Date.now();

        // Determine if face is visible
        const isFaceVisible = Array.isArray(preds) && 
                             preds.length === 1 && 
                             preds[0] !== null && 
                             preds[0] !== undefined &&
                             preds[0].topLeft && 
                             preds[0].bottomRight;

        // Update face detection status callback
        if (onFaceDetectionStatus && lastFaceStatusRef.current !== isFaceVisible) {
          lastFaceStatusRef.current = isFaceVisible;
          onFaceDetectionStatus(isFaceVisible);
        }

        // Handle based on mode - use current precheck value from props
        // Debug: Log mode check
        if (process.env.NODE_ENV === 'development') {
          console.log('🔀 Mode check:', {
            precheck,
            predictionsCount: preds?.length || 0,
            willCall: precheck ? 'handlePrecheck' : 'handleMonitoring'
          });
        }
        
        // Use current precheck value - it's in the closure from the effect
        if (precheck) {
          handlePrecheck(preds);
        } else {
          // Debug: Log before calling handleMonitoring
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 Calling handleMonitoring with:', {
              predictionsCount: preds?.length || 0,
              isArray: Array.isArray(preds),
              hasVideo: !!video,
              videoWidth: video?.videoWidth,
              videoHeight: video?.videoHeight
            });
          }
          handleMonitoring(preds, video);
        }
      } catch (err) {
        if (!err.message || !err.message.includes('height')) {
          console.error('Face detection error:', err);
        }
      }

      if (mounted) {
        rafId = requestAnimationFrame(detect);
      }
    };

    detect();

    return () => {
      mounted = false;
      if (rafId) cancelAnimationFrame(rafId);
      isInitializingRef.current = true;
      monitoringStartTimeRef.current = null;
      sideFaceCountRef.current = 0;
      forwardFaceCountRef.current = 0;
      noFaceCountRef.current = 0;
      multipleFacesCountRef.current = 0;
      detectionFailureCountRef.current = 0;
      lastSuccessfulDetectionRef.current = null;
    };
  }, [model, precheck, hasPermissions]);

  // Reset stable frames when precheck mode changes
  useEffect(() => {
    if (precheck) {
      stableFramesRef.current = 0;
      readyCalledRef.current = false;
      setPrecheckProgress(0);
    }
  }, [precheck]);

  return (
    <div style={{ position: 'relative' }}>
      <video
        ref={videoRef}
        width={320}
        height={240}
        style={{ borderRadius: 8, background: '#000', display: 'block' }}
        autoPlay
        muted
        playsInline
      />
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          borderRadius: 8,
          zIndex: 5
        }}
      />
      {!precheck && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: 8,
            zIndex: 10,
            background: detectionStatus === 'running' ? 'transparent' :
              detectionStatus === 'error' ? 'rgba(239, 68, 68, 0.9)' :
                'rgba(107, 114, 128, 0.9)',
            color: '#ffffff',
            padding: detectionStatus !== 'running' ? '4px 8px' : '0px',
            
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(4px)'
          }}
        >
          {detectionStatus && '🔴'}
        </div>
      )}
      {/* {!precheck && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            zIndex: 15,
            background: 'rgba(0, 0, 0, 0.85)',
            color: '#ffffff',
            padding: '8px',
            borderRadius: 4,
            fontSize: 10,
            fontFamily: 'monospace',
            maxWidth: '220px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
            lineHeight: '1.4'
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '4px', borderBottom: '1px solid #555', paddingBottom: '4px' }}>
            Face Detection Status
          </div>
          <div>Detector: {model ? '✅ Loaded' : '❌ Not Loaded'}</div>
          <div>Video: {videoRef.current ? '✅' : '❌'}</div>
          <div>Permissions: {hasPermissions ? '✅' : '❌'}</div>
          <div>Status: <span style={{ color: detectionStatus === 'running' ? '#10b981' : detectionStatus === 'error' ? '#ef4444' : '#fbbf24' }}>{detectionStatus}</span></div>
          <div>Face: {hasFaceDetected ? '✅ Detected' : '❌ Not Detected'}</div>
          {videoRef.current && (
            <>
              <div>Size: {videoRef.current.videoWidth}x{videoRef.current.videoHeight}</div>
              <div>Ready: {videoRef.current.readyState >= 2 ? '✅' : '⏳'}</div>
              <div>Playing: {videoRef.current.paused ? '❌ Paused' : '✅ Playing'}</div>
              <div>Stream: {videoRef.current.srcObject?.active ? '✅ Active' : '❌ Inactive'}</div>
            </>
          )}
        </div>
      )} */}
      {hasFaceDetected && (
        <div
          style={{
            position: 'absolute',
            bottom: 20,
            left: 0,
            right: 8,
            zIndex: 10,
            background: isLookingAtCamera ? 'rgba(16, 185, 129, 0.9)' : 'rgba(239, 68, 68, 0.9)',
            color: '#ffffff',
            padding: '2px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            backdropFilter: 'blur(4px)',
            transition: 'background 0.3s ease',
            width: "120px"
          }}
        >
          {isLookingAtCamera ? '👁️ Looking at 📷' : '⚠️ Not 👁️ at 📷'}
        </div>
      )}
      {badgeState && badgeLabel && (
        <span
          className={`proctoring-floating-panel__badge ${badgeState}`}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10
          }}
        >
          {badgeLabel}
        </span>
      )}
      {precheck && (
        <div style={{ marginTop: 8, fontSize: 14, color: '#4b5563' }}>
          {precheckProgress < 30 ? (
            <div>
              <div>Positioning face... ({Math.min(30, precheckProgress)}/30)</div>
              <div style={{
                width: '100%',
                height: 4,
                background: '#e5e7eb',
                borderRadius: 2,
                marginTop: 4,
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${(precheckProgress / 30) * 100}%`,
                  height: '100%',
                  background: '#3b82f6',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          ) : (
            <div style={{ color: '#10b981', fontWeight: 600 }}>
              ✓ Face detected! Ready to proceed.
            </div>
          )}
        </div>
      )}
      {!precheck && maxViolations > 0 && (
        <div className="proctoring-floating-panel__footer">
          <span>Violations</span>
          <span className={`proctoring-floating-panel__violations ${isCriticalViolations ? 'critical' : ''}`}>
            {violationCount || 0} / {maxViolations}
          </span>
        </div>
      )}
    </div>
  );
}